import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { formatIDR, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Phone, Building2, MapPin, MessageCircle, Receipt } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", company: "", address: "" });
  const [detail, setDetail] = useState(null);
  const [detailOrders, setDetailOrders] = useState([]);

  const load = useCallback(() => { api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name) { toast.error("Nama wajib diisi"); return; }
    try { await api.post("/customers", form); toast.success("Pelanggan ditambahkan"); setOpen(false); setForm({ name: "", phone: "", company: "", address: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openDetail = async (c) => {
    setDetail(c);
    try { const r = await api.get(`/customers/${c.id}/orders`); setDetailOrders(r.data); } catch { setDetailOrders([]); }
  };

  const filtered = customers.filter((c) => [c.name, c.company, c.phone].join(" ").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">Database Pelanggan</h1>
          <p className="text-sm text-slate-500">Riwayat order & total belanja setiap pelanggan.</p>
        </div>
        <Button data-testid="btn-add-customer" className="bg-blue-600 hover:bg-blue-700" onClick={() => setOpen(true)}><Plus className="mr-2 h-4 w-4" /> Tambah Pelanggan</Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="input-customer-search" className="pl-9" placeholder="Cari nama / perusahaan..." value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <Card key={c.id} data-testid={`customer-card-${c.id}`} className="p-5 border-slate-200/80 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDetail(c)}>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-blue-600 font-bold">{c.name[0]?.toUpperCase()}</div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900 truncate">{c.name}</p>
                <p className="text-xs text-slate-500 truncate flex items-center gap-1"><Building2 className="h-3 w-3" /> {c.company || "-"}</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-center">
              <div className="rounded-lg bg-slate-50 py-2"><p className="font-heading text-lg font-extrabold text-slate-900">{c.order_count}</p><p className="text-[11px] text-slate-500">Order</p></div>
              <div className="rounded-lg bg-slate-50 py-2"><p className="font-heading text-sm font-extrabold text-emerald-600">{formatIDR(c.lifetime_spend)}</p><p className="text-[11px] text-slate-500">Total Belanja</p></div>
            </div>
            {c.phone && (
              <button data-testid={`wa-customer-${c.id}`} onClick={(e) => { e.stopPropagation(); window.open(`https://wa.me/${c.phone}`, "_blank"); }}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors">
                <MessageCircle className="h-3.5 w-3.5" /> {c.phone}
              </button>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Tambah Pelanggan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nama *</Label><Input data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>No. WhatsApp</Label><Input data-testid="customer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="6281..." /></div>
            <div className="space-y-1.5"><Label>Perusahaan / Komunitas</Label><Input data-testid="customer-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Alamat</Label><Input data-testid="customer-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="btn-save-customer" className="bg-blue-600 hover:bg-blue-700" onClick={save}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{detail?.name}</DialogTitle></DialogHeader>
          <div className="space-y-1 text-sm text-slate-600">
            {detail?.company && <p className="flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /> {detail.company}</p>}
            {detail?.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {detail.phone}</p>}
            {detail?.address && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {detail.address}</p>}
          </div>
          <div className="mt-2">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Riwayat Order ({detailOrders.length})</p>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {detailOrders.map((o) => (
                <div key={o.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2">
                  <div><p className="font-mono text-xs text-blue-600">{o.order_id}</p><p className="text-sm text-slate-700">{o.design_name}</p></div>
                  <div className="text-right"><p className="text-sm font-semibold text-slate-800">{formatIDR(o.total)}</p><StatusBadge value={o.payment_status} kind="payment" /></div>
                </div>
              ))}
              {detailOrders.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Belum ada order.</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
